import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  Firestore,
  DocumentReference,
  arrayUnion,
  collection,
  doc,
  increment,
  serverTimestamp,
  setDoc
} from '@angular/fire/firestore';

/* Sections of the public website, in page order.
   The key is what gets stored in Firestore (visitorlogs/*.sections) and must match the
   data-track-section attribute in mainwebpage.component.html — never rename a key,
   only change the label. If you add a section, also add its key to the Firestore rules. */
export const TRACKED_SECTIONS: { key: string; label: string }[] = [
  { key: 'hero', label: 'Home' },
  { key: 'services', label: 'Services' },
  { key: 'contact', label: 'Contact Info' },
  { key: 'about', label: 'About' },
  { key: 'team', label: 'Our Team' },
  { key: 'testimonials', label: 'Testimonials' },
  { key: 'whyus', label: 'Why Choose Us' },
  { key: 'faq', label: 'FAQ' },
  { key: 'appointment', label: 'Book Appointment' },
  { key: 'footer', label: 'Footer' }
];

const STORAGE_KEYS = {
  visitorId: 'ep_visitor_id',      // localStorage: anonymous id for this browser
  visitCount: 'ep_visit_count',    // localStorage: how many visits this browser made
  optOut: 'ep_skip_tracking',      // localStorage: '1' = never track this browser (set from admin page)
  session: 'ep_visit_session'      // sessionStorage: the current visit, so reloads continue it
};

const TICK_MS = 1000;
const FLUSH_EVERY_SECONDS = 60;           // save to Firestore after this many counted seconds
const EARLY_FLUSH_AT = [5, 15];           // ...and early on, so short visits are saved before the tab closes
const IDLE_LIMIT_MS = 2 * 60 * 1000;      // stop counting after 2 min without scroll/mouse/touch/keys
const SESSION_RESUME_MS = 30 * 60 * 1000; // a reload within 30 min continues the same visit
const ACTIVITY_EVENTS = ['scroll', 'mousemove', 'keydown', 'touchstart', 'click'];

interface VisitState {
  docId: string;
  visitorId: string;
  isReturning: boolean;
  visitNumber: number;
  durationSeconds: number;                 // counted so far in this tab
  sectionOrder: string[];                  // section keys in the order first viewed
  exitSection: string;                     // last section viewed
  bookedAppointment: boolean;
  pendingSeconds: number;                  // counted but not saved yet
  pendingSections: Record<string, number>; // section key → seconds counted but not saved yet
  lastActiveMs: number;
  created: boolean;                        // first write reached Firestore
}

/* Records one document per visit in Firestore: visitorlogs/{autoId}
   { visitorId, isReturning, visitNumber, startedAt, lastActiveAt, durationSeconds,
     sections: { services: 42, ... }, sectionOrder, exitSection, bookedAppointment,
     page, device, os, browser, screen, language, timeZone, referrer, utmSource, utmMedium, utmCampaign }
   Seconds are saved as increments, so two tabs on one visit (or a retried write) add up
   instead of overwriting each other. Anonymous: no name, phone, email, IP or location is stored. */
@Injectable({ providedIn: 'root' })
export class VisitorTrackingService {

  private firestore = inject(Firestore);
  private zone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);

  private state: VisitState | null = null;
  private ref: DocumentReference | null = null;
  private running = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private sectionEls: HTMLElement[] = [];
  private lastInteraction = 0;
  private secondsSinceFlush = 0;
  private dirty = false;
  private writing = false;
  private flushQueued = false;
  private warned = false;

  /* ---------------- PUBLIC API ---------------- */

  start(): void {
    if (!isPlatformBrowser(this.platformId) || this.running) return;
    if (this.isOptedOut() || this.isBot()) return;

    // coming back to '/' in the same tab continues the visit held in memory
    const current = this.state && Date.now() - this.state.lastActiveMs <= SESSION_RESUME_MS ? this.state : null;

    this.running = true;
    this.state = current ?? this.resumeSession() ?? this.newSession();
    this.ref = doc(this.firestore, 'visitorlogs', this.state.docId);
    this.sectionEls = [];
    this.lastInteraction = Date.now();
    this.secondsSinceFlush = 0;

    // outside Angular so the 1-second timer doesn't trigger change detection
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => this.tick(), TICK_MS);
      ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, this.onActivity, { passive: true }));
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('pagehide', this.onPageHide);
    });

    // save straight away so even very short visits are recorded
    this.dirty = true;
    this.flush();
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;

    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, this.onActivity));
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pagehide', this.onPageHide);
    this.sectionEls = [];

    this.flush();
  }

  markAppointmentBooked(): void {
    if (!this.state || !this.running) return;
    this.state.bookedAppointment = true;
    this.dirty = true;
    this.flush();
  }

  isOptedOut(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    return this.read('local', STORAGE_KEYS.optOut) === '1';
  }

  setOptOut(optOut: boolean): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.write('local', STORAGE_KEYS.optOut, optOut ? '1' : null);

    if (optOut) {
      // forget the current visit so nothing more is written to it
      this.stop();
      this.state = null;
      this.ref = null;
      this.write('session', STORAGE_KEYS.session, null);
    }
  }

  /* ---------------- COUNTING ---------------- */

  private onActivity = (): void => {
    this.lastInteraction = Date.now();
  };

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.flush();
    } else {
      // coming back to the tab counts as activity
      this.lastInteraction = Date.now();
    }
  };

  private onPageHide = (): void => {
    this.flush();
  };

  private tick(): void {
    const s = this.state;
    if (!s) return;

    // only count time the visitor is actually looking at the page
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - this.lastInteraction > IDLE_LIMIT_MS) return;

    s.durationSeconds++;
    s.pendingSeconds++;
    s.lastActiveMs = now;

    const key = this.activeSection();
    if (key) {
      s.pendingSections[key] = (s.pendingSections[key] || 0) + 1;
      if (!s.sectionOrder.includes(key)) s.sectionOrder.push(key);
      s.exitSection = key;
    }

    this.dirty = true;
    this.secondsSinceFlush++;
    if (this.secondsSinceFlush >= FLUSH_EVERY_SECONDS || EARLY_FLUSH_AT.includes(s.durationSeconds)) {
      this.flush();
    }
  }

  /* the section taking up the most of the screen right now */
  private activeSection(): string {
    if (!this.sectionEls.length) {
      this.sectionEls = Array.from(document.querySelectorAll<HTMLElement>('[data-track-section]'));
    }

    const viewportHeight = window.innerHeight;
    let best = '';
    let bestVisible = 0;

    for (const el of this.sectionEls) {
      const rect = el.getBoundingClientRect();
      const visible = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
      if (visible > bestVisible) {
        bestVisible = visible;
        best = el.dataset['trackSection'] || '';
      }
    }

    return best;
  }

  /* ---------------- SAVING ---------------- */

  private async flush(): Promise<void> {
    const s = this.state;
    const ref = this.ref;
    if (!s || !ref || !this.dirty) return;

    if (this.writing) {
      // keep the newest counts in sessionStorage while the earlier write finishes
      this.saveSession();
      this.flushQueued = true;
      return;
    }

    this.writing = true;
    this.dirty = false;
    this.secondsSinceFlush = 0;

    // this write takes the unsaved counts; if it fails they go back to pending
    const sentSeconds = s.pendingSeconds;
    const sentSections = s.pendingSections;
    s.pendingSeconds = 0;
    s.pendingSections = {};
    this.saveSession();

    try {
      await setDoc(ref, this.payload(s, sentSeconds, sentSections), { merge: true });
      s.created = true;
    } catch (err) {
      s.pendingSeconds += sentSeconds;
      for (const [key, secs] of Object.entries(sentSections)) {
        s.pendingSections[key] = (s.pendingSections[key] || 0) + secs;
      }
      this.dirty = true;
      // tracking must never break the website — warn once and keep going
      if (!this.warned) {
        this.warned = true;
        console.warn('Visitor log could not be saved', err);
      }
    } finally {
      this.writing = false;
      this.saveSession();
      if (this.flushQueued) {
        this.flushQueued = false;
        this.flush();
      }
    }
  }

  private payload(s: VisitState, seconds: number, sections: Record<string, number>): Record<string, unknown> {
    const data: Record<string, unknown> = {
      durationSeconds: increment(seconds),
      exitSection: s.exitSection,
      lastActiveAt: serverTimestamp()
    };

    // only send fields that have something to add — with merge, an empty map or a
    // `false` would overwrite what another tab (or an earlier write) already saved
    const sectionIncrements: Record<string, unknown> = {};
    for (const [key, secs] of Object.entries(sections)) {
      if (secs > 0) sectionIncrements[key] = increment(secs);
    }
    if (Object.keys(sectionIncrements).length) data['sections'] = sectionIncrements;
    if (s.sectionOrder.length) data['sectionOrder'] = arrayUnion(...s.sectionOrder);
    if (s.bookedAppointment) data['bookedAppointment'] = true;

    // details that never change during a visit are only sent with the first write
    if (!s.created) {
      Object.assign(data, {
        visitorId: s.visitorId,
        isReturning: s.isReturning,
        visitNumber: s.visitNumber,
        startedAt: serverTimestamp(),
        page: location.pathname.slice(0, 100),
        ...this.deviceInfo(),
        ...this.trafficSource()
      });
    }

    return data;
  }

  /* ---------------- SESSION ---------------- */

  private newSession(): VisitState {
    let visitorId = this.read('local', STORAGE_KEYS.visitorId);
    const isReturning = !!visitorId;
    if (!visitorId) {
      visitorId = this.randomId();
      this.write('local', STORAGE_KEYS.visitorId, visitorId);
    }

    const visitNumber = (Number(this.read('local', STORAGE_KEYS.visitCount)) || 0) + 1;
    this.write('local', STORAGE_KEYS.visitCount, String(visitNumber));

    return {
      docId: doc(collection(this.firestore, 'visitorlogs')).id,
      visitorId,
      isReturning,
      visitNumber,
      durationSeconds: 0,
      sectionOrder: [],
      exitSection: '',
      bookedAppointment: false,
      pendingSeconds: 0,
      pendingSections: {},
      lastActiveMs: Date.now(),
      created: false
    };
  }

  private resumeSession(): VisitState | null {
    const raw = this.read('session', STORAGE_KEYS.session);
    if (!raw) return null;

    try {
      const s = JSON.parse(raw) as VisitState;
      const valid = typeof s.docId === 'string' && !!s.docId
        && typeof s.visitorId === 'string'
        && typeof s.durationSeconds === 'number'
        && typeof s.pendingSeconds === 'number'
        && typeof s.lastActiveMs === 'number'
        && !!s.pendingSections && typeof s.pendingSections === 'object'
        && Array.isArray(s.sectionOrder);
      if (!valid || Date.now() - s.lastActiveMs > SESSION_RESUME_MS) return null;
      return s;
    } catch {
      return null;
    }
  }

  private saveSession(): void {
    if (this.state) this.write('session', STORAGE_KEYS.session, JSON.stringify(this.state));
  }

  /* ---------------- VISITOR DETAILS ---------------- */

  private isBot(): boolean {
    const ua = navigator.userAgent || '';
    return navigator.webdriver === true || /bot|crawl|spider|slurp|lighthouse|headless|pagespeed/i.test(ua);
  }

  private deviceInfo(): Record<string, string> {
    const ua = navigator.userAgent || '';
    // iPadOS reports itself as a Mac — touch support gives it away
    const isIpad = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);

    const device = isIpad || /Tablet/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))
      ? 'tablet'
      : /Mobi|iPhone|iPod|Android/i.test(ua) ? 'mobile' : 'desktop';

    const os = isIpad || /iPhone|iPod/i.test(ua) ? 'iOS'
      : /Android/i.test(ua) ? 'Android'
      : /Windows/i.test(ua) ? 'Windows'
      : /Mac OS X|Macintosh/i.test(ua) ? 'macOS'
      : /CrOS/i.test(ua) ? 'ChromeOS'
      : /Linux/i.test(ua) ? 'Linux'
      : 'Other';

    const browser = /Edg\//i.test(ua) ? 'Edge'
      : /OPR\/|Opera/i.test(ua) ? 'Opera'
      : /SamsungBrowser/i.test(ua) ? 'Samsung Internet'
      : /Firefox|FxiOS/i.test(ua) ? 'Firefox'
      : /Chrome|CriOS/i.test(ua) ? 'Chrome'
      : /Safari/i.test(ua) ? 'Safari'
      : 'Other';

    let timeZone = '';
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch { /* not supported */ }

    return {
      device,
      os,
      browser,
      screen: `${screen.width}x${screen.height}`.slice(0, 20),
      language: (navigator.language || '').slice(0, 20),
      timeZone: timeZone.slice(0, 50)
    };
  }

  private trafficSource(): Record<string, string> {
    // only the referring site's domain is kept, never its full URL
    let referrer = '';
    try {
      if (document.referrer) {
        const host = new URL(document.referrer).hostname;
        if (host && host !== location.hostname) referrer = host;
      }
    } catch { /* invalid referrer */ }

    const params = new URLSearchParams(location.search);
    return {
      referrer: referrer.slice(0, 100),
      utmSource: (params.get('utm_source') || '').slice(0, 100),
      utmMedium: (params.get('utm_medium') || '').slice(0, 100),
      utmCampaign: (params.get('utm_campaign') || '').slice(0, 100)
    };
  }

  /* ---------------- HELPERS ---------------- */

  private randomId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  // storage can throw (private mode, blocked cookies) — tracking just carries on without it
  private read(store: 'local' | 'session', key: string): string | null {
    try {
      return (store === 'local' ? localStorage : sessionStorage).getItem(key);
    } catch {
      return null;
    }
  }

  private write(store: 'local' | 'session', key: string, value: string | null): void {
    try {
      const storage = store === 'local' ? localStorage : sessionStorage;
      if (value === null) storage.removeItem(key);
      else storage.setItem(key, value);
    } catch { /* ignore */ }
  }
}
